// src/pages/creator-panel/ChatAnalysis/SessionLogsDrawer.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { formatDistanceToNow, parseISO } from "date-fns";

const DRAWER_WIDTH = { xs: "100%", sm: 640 };

export default function SessionLogsDrawer({ open, onClose, chatbotId, sessionUid }) {
  // meta: { exists, content, truncated, size_bytes, last_uploaded_at }
  const [meta, setMeta] = useState(null);
  const [search, setSearch] = useState("");
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [error, setError] = useState(null);

  const [downloading, setDownloading] = useState(false);

  // Download via axios so the Authorization header is sent (a plain
  // <a href> navigation would not include it and would 401). For files
  // already loaded inline, reuse the content we have in memory instead
  // of round-tripping to GCS again.
  const handleDownload = useCallback(async () => {
    if (!chatbotId || !sessionUid || !meta?.exists) return;
    setDownloading(true);
    try {
      let blob;
      if (meta.content != null && !meta.truncated) {
        blob = new Blob([meta.content], { type: "text/plain;charset=utf-8" });
      } else {
        const res = await axiosInstance.get(
          `/api/chat-analysis/session-logs/${encodeURIComponent(sessionUid)}/download`,
          { params: { chatbot_id: chatbotId }, responseType: "blob" }
        );
        blob = res.data;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sessionUid}.log`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  }, [chatbotId, sessionUid, meta]);

  const fetchLogs = useCallback(async () => {
    if (!open || !chatbotId || !sessionUid) return;
    setError(null);
    setLoadingMeta(true);
    try {
      // Content comes back inline in this single call — klonit-api
      // proxies it from GCS so the browser never has to make a
      // cross-origin fetch (which would require per-bucket CORS).
      const res = await axiosInstance.get(
        `/api/chat-analysis/session-logs/${encodeURIComponent(sessionUid)}`,
        { params: { chatbot_id: chatbotId } }
      );
      setMeta(res.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load logs.");
      setMeta(null);
    } finally {
      setLoadingMeta(false);
    }
  }, [open, chatbotId, sessionUid]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset state when the drawer closes so the next open re-fetches fresh.
  useEffect(() => {
    if (!open) {
      setMeta(null);
      setSearch("");
      setError(null);
    }
  }, [open]);

  const filteredText = useMemo(() => {
    const text = meta?.content || "";
    if (!text) return "";
    const q = search.trim().toLowerCase();
    if (!q) return text;
    return text
      .split("\n")
      .filter((line) => line.toLowerCase().includes(q))
      .join("\n");
  }, [meta?.content, search]);

  const updatedAgo = useMemo(() => {
    if (!meta?.last_uploaded_at) return null;
    try {
      return formatDistanceToNow(parseISO(meta.last_uploaded_at), { addSuffix: true });
    } catch {
      return null;
    }
  }, [meta?.last_uploaded_at]);

  const sizeKB = meta?.size_bytes ? (meta.size_bytes / 1024).toFixed(1) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: DRAWER_WIDTH } }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Session Logs</Typography>
          <Typography variant="caption" color="text.secondary">
            {sessionUid || "—"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={fetchLogs} disabled={loadingMeta} title="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
        {meta?.exists && (
          <IconButton
            size="small"
            onClick={handleDownload}
            disabled={downloading}
            title="Download .log"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton size="small" onClick={onClose} title="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Filter lines…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={!meta?.content}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {meta?.exists && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {sizeKB ? `${sizeKB} KB` : null}
            {sizeKB && updatedAgo ? " · " : null}
            {updatedAgo ? `updated ${updatedAgo}` : null}
          </Typography>
        )}
      </Box>

      <Box sx={{ p: 2, flex: 1, overflow: "auto" }}>
        {(loadingMeta) && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loadingMeta && error && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {error}
            </Typography>
            <Button size="small" variant="outlined" onClick={fetchLogs} startIcon={<RefreshIcon />}>
              Retry
            </Button>
          </Box>
        )}

        {!loadingMeta && !error && meta && meta.exists === false && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {meta.message ||
                "No logs uploaded yet. Logs are synced every 5 minutes — try again shortly."}
            </Typography>
            <Button size="small" variant="outlined" onClick={fetchLogs} startIcon={<RefreshIcon />}>
              Check again
            </Button>
          </Box>
        )}

        {!loadingMeta && !error && meta?.exists && meta?.truncated && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This log file is too large to preview inline ({sizeKB} KB). Use Download to view it.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDownload}
              disabled={downloading}
              startIcon={<DownloadIcon />}
            >
              Download .log
            </Button>
          </Box>
        )}

        {!loadingMeta && !error && meta?.exists && !meta?.truncated && (
          <Box
            component="pre"
            sx={{
              fontFamily:
                'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
              fontSize: 12,
              lineHeight: 1.5,
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              color: "text.primary",
            }}
          >
            {filteredText || (
              <Typography variant="body2" color="text.secondary">
                No lines match this filter.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
